import api from './api';
import imageCompression from 'browser-image-compression';
import { INSPECTION_STATUS_VERDICT, INSPECTION_STATUS } from '../utils/constants';

const { PENDING } = INSPECTION_STATUS_VERDICT;
const { PENDING_REVIEW } = INSPECTION_STATUS;

class PhotoService {
    // Comprimir imagen
    async compressImageForS3(imageFile, options = {}) {
        const {
            maxSizeMB = 1,           // Tamaño máximo en MB
            maxWidthOrHeight = 1920, // Dimensión máxima
            useWebWorker = true,      // Usar Web Worker para mejor performance
            fileType = 'image/jpeg'   // Tipo de archivo de salida
        } = options;

        try {
            console.log('Comprimiendo imagen...', {
                originalSize: (imageFile.size / 1024 / 1024).toFixed(2) + ' MB',
                originalType: imageFile.type
            });

            const compressedFile = await imageCompression(imageFile, {
                maxSizeMB,
                maxWidthOrHeight,
                useWebWorker,
                fileType
            });

            console.log('Imagen comprimida exitosamente:', {
                compressedSize: (compressedFile.size / 1024 / 1024).toFixed(2) + ' MB',
                compressionRatio: ((1 - compressedFile.size / imageFile.size) * 100).toFixed(1) + '%'
            });

            return compressedFile;
        } catch (error) {
            console.error('Error comprimiendo imagen:', error);
            // Si falla la compresión, retornar el archivo original
            return imageFile;
        }
    }

    // Generar URLs de upload
    async generateUploadUrls(inspectionId, photoCount, updatePhotos) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/upload-urls`, {
                photoCount: photoCount,
                inspectionId: inspectionId,
                updatePhotos: updatePhotos
            });
            return response.data;
        } catch (error) {
            // Extraer mensaje de error del backend
            const errorMessage = error.response?.data?.error || 'Error generando URLs de upload';

            // Crear error con mensaje descriptivo
            const customError = new Error(errorMessage);
            customError.response = error.response;
            customError.status = error.response?.status;

            throw customError;
        }
    }

    // Subir foto a S3
    async uploadPhotoToS3(uploadUrl, file, onProgress) {
        try {
            const compressedFile = await this.compressImageForS3(file);

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable && onProgress) {
                        const progress = (event.loaded / event.total) * 100;
                        onProgress(progress);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({
                            success: true,
                            originalSize: file.size,
                            compressedSize: compressedFile.size
                        });
                    } else {
                        reject(new Error(`Upload failed: ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload failed'));
                });

                xhr.open('PUT', uploadUrl.uploadUrl);
                xhr.setRequestHeader('Content-Type', 'image/jpeg');
                xhr.send(compressedFile);
            });
        } catch (error) {
            console.error('Error uploading photo:', error);
            throw error;
        }
    }

    async updateExistingPhoto(file, inspectionId, photoId, onProgress) {
        try {
            const uploadUrlResponse = await this.generateUploadUrls(inspectionId, 1, [{ id: photoId }]);
            const uploadUrl = uploadUrlResponse.uploadUrls[0];
            await this.uploadPhotoToS3(uploadUrl, file, (progress) => {
                if (onProgress) {
                    onProgress(progress);
                }
            });
        } catch (error) {
            console.error('Error updating existing photo:', error);
            throw error;
        }
    }

    // Subir múltiples fotos
    async uploadMultiplePhotos(files, inspectionId, updatePhotos, onProgress) {
        try {
            // Generar URLs de upload
            const uploadUrlsResponse = await this.generateUploadUrls(inspectionId, files.length, updatePhotos);
            const uploadUrls = uploadUrlsResponse.uploadUrls;

            const uploadPromises = files.map(async (file, index) => {
                const uploadUrl = uploadUrls[index];

                return new Promise(async (resolve) => {
                    try {
                        const result = await this.uploadPhotoToS3(uploadUrl, file, (progress) => {
                            if (onProgress) {
                                onProgress(index, progress);
                            }
                        });

                        resolve({
                            ...uploadUrl,
                            ...result,
                            originalName: file.name
                        });
                    } catch (error) {
                        resolve({
                            ...uploadUrl,
                            error: error.message,
                            originalName: file.name
                        });
                    }
                });
            });
            const results = await Promise.all(uploadPromises);

            // Filtrar solo los uploads exitosos
            const successfulUploads = results.filter(result => !result.error);

            // Actualizar DynamoDB con las fotos subidas
            if (successfulUploads.length > 0) {
                await this.updatePhotosInDynamoDB(inspectionId, successfulUploads);
            }

            return {
                successful: successfulUploads,
                failed: results.filter(result => result.error),
                total: results.length
            };

        } catch (error) {
            console.error('Error uploading multiple photos:', error);
            throw error;
        }
    }

    // Subir fotos faltantes
    async uploadMissingPhotos(newPhotosFiles, inspectionId, onProgress) {
        try {
            const updatePhotos = newPhotosFiles.map(file => ({ id: file.id }));
            const files = newPhotosFiles.map(file => file.file);
            const uploadUrlsResponse = await this.generateUploadUrls(inspectionId, files.length, updatePhotos);
            const uploadUrls = uploadUrlsResponse.uploadUrls;

            const uploadPromises = files.map(async (file, index) => {
                const uploadUrl = uploadUrls[index];
                return this.uploadPhotoToS3(uploadUrl, file, (progress) => {
                    if (onProgress) {
                        onProgress(index, progress);
                    }
                });
            });

            const results = await Promise.all(uploadPromises);

            return results;
        }
        catch (error) {
            console.error('Error uploading missing photos:', error);
            throw error;
        }
    }

    // Actualizar fotos en DynamoDB
    async updatePhotosInDynamoDB(inspectionId, photos) {
        try {
            const photosData = photos.map(photo => ({
                key: photo.s3Key,
                name: photo.name,
                id: photo.id,
                status: PENDING,
                uploadedAt: new Date().toISOString()
            }));

            const response = await api.put(`/inspections/${inspectionId}/photos`, {
                inspectionId: inspectionId,
                photos: photosData,
                status: PENDING_REVIEW
            });

            return response.data;
        } catch (error) {
            console.error('Error updating photos in DynamoDB:', error);
            throw new Error(error.response?.data?.error || 'Error actualizando fotos en la base de datos');
        }
    }

    // Obtener URL de descarga de foto
    getPhotoUrl(s3Key) {
        return `${process.env.REACT_APP_S3_BASE_URL}/${s3Key}`;
    }

    // Descargar y mejorar foto
    async downloadAndEnhancePhoto(s3Key) {
        try {
            const response = await fetch(this.getPhotoUrl(s3Key));
            const blob = await response.blob();

            // Aplicar mejoras de imagen
            return await this.optimizeImageForDisplay(blob);
        } catch (error) {
            console.error('Error downloading photo:', error);
            throw error;
        }
    }

    // Mejorar imagen
    enhanceImage(blob) {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0);

                // Aplicar mejoras básicas
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const enhanced = this.applyEnhancements(imageData);
                ctx.putImageData(enhanced, 0, 0);

                canvas.toBlob((enhancedBlob) => {
                    resolve(enhancedBlob);
                }, 'image/jpeg', 0.95);
            };

            img.src = URL.createObjectURL(blob);
        });
    }

    // Aplicar mejoras de imagen
    applyEnhancements(imageData) {
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
            // Mejorar contraste
            const factor = 1.1;
            data[i] = Math.min(255, Math.max(0, (data[i] - 128) * factor + 128));
            data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * factor + 128));
            data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * factor + 128));

            // Ajustar brillo ligeramente
            data[i] = Math.min(255, data[i] * 1.05);
            data[i + 1] = Math.min(255, data[i + 1] * 1.05);
            data[i + 2] = Math.min(255, data[i + 2] * 1.05);
        }
    }

    async downloadPhotosZip(inspectionId) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/photos/download-zip`, {
                inspectionId: inspectionId
            });
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            console.error('Error downloading photos ZIP:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al generar el archivo ZIP'
            };
        }
    }

    async downloadPhoto(inspectionId, photoKey) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/photos/download`, {
                inspectionId: inspectionId,
                photoKey: photoKey
            });
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            console.error('Error downloading photo:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al generar la descarga de la foto'
            };
        }
    }
}

const photoService = new PhotoService();
export default photoService;